import React, { useState, useEffect, useRef } from "react";
import { buildApiUrl } from '../config';
import "./ServiceTransaction.css";
import "./Expenses.css";

const Expenses = () => {
  // Radio button state for switching between Expenses and Overtime Payment
  const [entryType, setEntryType] = useState('expenses');

  // Expenses states
  const [date, setDate] = useState("");
  const [description, setDescription] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("credit");
  const [expenseType, setExpenseType] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [trnNumber, setTrnNumber] = useState("");
  const [address, setAddress] = useState("");
  const [isOtherExpenseType, setIsOtherExpenseType] = useState(false);
  const [isOtherSupplier, setIsOtherSupplier] = useState(false);

  // Overtime Payment states
  const [overtimeDate, setOvertimeDate] = useState("");
  const [workerName, setWorkerName] = useState("");
  const [workerNames, setWorkerNames] = useState([]);
  const [isOtherWorker, setIsOtherWorker] = useState(false);
  const [overtimeAmount, setOvertimeAmount] = useState("");
  const [overtimeNotes, setOvertimeNotes] = useState("");

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const expenseTypesRes = await fetch(
          buildApiUrl("api/expensetypes")
        );
        const suppliersRes = await fetch(
          buildApiUrl("api/expenses/suppliers")
        );
        const workersRes = await fetch(
          buildApiUrl("api/overtime/workers")
        );

        const expenseTypesData = await expenseTypesRes.json();
        const suppliersData = await suppliersRes.json();
        const workersData = await workersRes.json();

        setExpenseTypes(expenseTypesData);
        setSuppliers(suppliersData);

        // Handle the response format from the API
        if (workersData.success && workersData.data) {
          const workerNamesList = workersData.data.map(worker => worker.WorkerName);
          setWorkerNames(workerNamesList);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleExpenseTypeChange = (event) => {
    const selectedType = event.target.value;
    setExpenseType(selectedType);
    setIsOtherExpenseType(selectedType === "Other");
  };

  const handleSupplierNameChange = (event) => {
    const selectedSupplier = event.target.value;
    setSupplierName(selectedSupplier);

    if (selectedSupplier === "Other") {
      setIsOtherSupplier(true);
      setContactNumber("");
      setTrnNumber("");
      setAddress("");
    } else {
      setIsOtherSupplier(false);
      const supplierDetails = suppliers.find(
        (supplier) => supplier.name === selectedSupplier
      );
      if (supplierDetails) {
        setContactNumber(supplierDetails.ContactNumber || "");
        setTrnNumber(supplierDetails.TRN || "");
        setAddress(supplierDetails.Address || "");
      }
    }
  };

  const handleWorkerNameChange = (event) => {
    const selectedWorker = event.target.value;
    setWorkerName(selectedWorker);
    setIsOtherWorker(selectedWorker === "Other");
  };

  const handleAddExpense = async () => {
    if (isSubmittingRef.current) return;

    const expenseData = {
      Date: date,
      Description: description || `Purchase of "${expenseType}" from ${supplierName} via ${paymentMethod}.`,
      ExpenseAmount: parseFloat(expenseAmount) || 0,
      PaymentMethod: paymentMethod,
      ExpenseType: isOtherExpenseType ? "Other" : expenseType,
      NewExpenseType: isOtherExpenseType ? expenseType : null,
      SupplierName: supplierName,
      ContactNumber: isOtherSupplier ? contactNumber : undefined,
      TRN: isOtherSupplier ? trnNumber : undefined,
      Address: isOtherSupplier ? address : undefined,
    };

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (isOtherExpenseType) {
        await fetch(buildApiUrl("api/expensetypes/add"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ TypeName: expenseType }),
        });
      }

      if (isOtherSupplier) {
        await fetch(buildApiUrl("api/expenses/suppliers/add"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            SupplierName: supplierName,
            ContactNumber: contactNumber,
            Address: address,
          }),
        });
      }

      const response = await fetch(buildApiUrl("api/expenses/add"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(expenseData),
      });

      const result = await response.json();

      if (result.success) {
        alert("Expense added successfully!");
        setDate("");
        setDescription("");
        setExpenseAmount("");
        setPaymentMethod("credit");
        setExpenseType("");
        setSupplierName("");
        setContactNumber("");
        setTrnNumber("");
        setAddress("");
      } else {
        throw new Error(result.error || "Failed to add expense.");
      }
    } catch (error) {
      console.error("Error adding expense:", error);
      alert("Failed to add expense. Please check the input and try again.");
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleAddOvertimePayment = async () => {
    if (isSubmittingRef.current) return;

    const overtimeData = {
      Date: overtimeDate,
      WorkerName: workerName,
      OvertimeAmount: parseFloat(overtimeAmount) || 0,
      Notes: overtimeNotes,
    };

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await fetch(buildApiUrl("api/overtime/add"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(overtimeData),
      });

      const result = await response.json();

      if (result.success) {
        alert("Overtime payment added successfully!");
        setOvertimeDate("");
        setWorkerName("");
        setIsOtherWorker(false);
        setOvertimeAmount("");
        setOvertimeNotes("");
      } else {
        throw new Error(result.error || "Failed to add overtime payment.");
      }
    } catch (error) {
      console.error("Error adding overtime payment:", error);
      alert("Failed to add overtime payment. Please check the input and try again.");
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="service-transaction-container">
        {/* Radio buttons for switching between Expenses and Overtime Payment */}
        <div className="radio-group">
          <label>
            <input
              type="radio"
              name="entryType"
              value="expenses"
              checked={entryType === 'expenses'}
              onChange={() => {
                setEntryType('expenses');
                // Reset overtime fields when switching
                setOvertimeDate('');
                setWorkerName('');
                setIsOtherWorker(false);
                setOvertimeAmount('');
                setOvertimeNotes('');
              }}
            />
            Expenses
          </label>
          <label>
            <input
              type="radio"
              name="entryType"
              value="overtime"
              checked={entryType === 'overtime'}
              onChange={() => {
                setEntryType('overtime');
                // Reset expense fields when switching
                setDate('');
                setDescription('');
                setExpenseAmount('');
                setPaymentMethod('credit');
                setExpenseType('');
                setSupplierName('');
                setContactNumber('');
                setTrnNumber('');
                setAddress('');
                setIsOtherExpenseType(false);
                setIsOtherSupplier(false);
              }}
            />
            Overtime Payment
          </label>
        </div>

        {entryType === 'expenses' ? (
          // Expenses Form
          <div className="form-content">
            <div className="form-grid">
              <div className="form-group">
                <label>Supplier Name:</label>
                {!isOtherSupplier ? (
                  <select value={supplierName} onChange={handleSupplierNameChange}>
                    <option value="">Select supplier</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.SupplierName}>
                        {supplier.SupplierName}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="Enter supplier name"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                  />
                )}
              </div>
              {isOtherSupplier && (
                <>
                  <div className="form-group">
                    <label>Contact Number:</label>
                    <input
                      type="text"
                      value={contactNumber}
                      onChange={(e) => setContactNumber(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>TRN:</label>
                    <input
                      type="text"
                      value={trnNumber}
                      onChange={(e) => setTrnNumber(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Address:</label>
                    <textarea
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>
                </>
              )}
              <div className="form-group">
                <label>Date:</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Expense Amount:</label>
                <input
                  type="text"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Payment Method:</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="credit">Credit</option>
                  <option value="debit">Debit</option>
                  <option value="cash">Cash</option>
                  <option value="pos">POS</option>
                </select>
              </div>
              <div className="form-group">
                <label>Expense Type:</label>
                {!isOtherExpenseType ? (
                  <select value={expenseType} onChange={handleExpenseTypeChange}>
                    <option value="">Select expense type</option>
                    {expenseTypes.map((type) => (
                      <option key={type.ExpenseTypeID} value={type.TypeName}>
                        {type.TypeName}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="Enter expense type"
                    value={expenseType}
                    onChange={(e) => setExpenseType(e.target.value)}
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          // Overtime Payment Form
          <div className="form-content">
            <div className="form-grid">
              <div className="form-group">
                <label>Date:</label>
                <input
                  type="date"
                  value={overtimeDate}
                  onChange={(e) => setOvertimeDate(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Worker Name:</label>
                {!isOtherWorker ? (
                  <select value={workerName} onChange={handleWorkerNameChange} required>
                    <option value="">Select worker name</option>
                    {workerNames.map((name, index) => (
                      <option key={index} value={name}>
                        {name}
                      </option>
                    ))}
                    <option value="Other">Other (Add New Worker)</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={workerName}
                    onChange={(e) => setWorkerName(e.target.value)}
                    placeholder="Enter new worker name"
                    required
                  />
                )}
              </div>
              <div className="form-group">
                <label>Overtime Amount:</label>
                <input
                  type="number"
                  value={overtimeAmount}
                  onChange={(e) => setOvertimeAmount(e.target.value)}
                  placeholder="Enter overtime amount"
                  required
                />
              </div>
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label>Notes:</label>
                <textarea
                  value={overtimeNotes}
                  onChange={(e) => setOvertimeNotes(e.target.value)}
                  placeholder="Add any additional notes here..."
                  rows="4"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Button container outside of form-content to ensure proper positioning */}
        <div className="button-container">
          {entryType === 'expenses' ? (
            <button onClick={handleAddExpense} className="add-expense-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Add Expense'}
            </button>
          ) : (
            <button onClick={handleAddOvertimePayment} className="add-expense-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Record'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Expenses;
